import * as React from 'react';
import Alert from '@mui/material/Alert';
import AvatarAthlete from '../Athletes/AvatarAthlete.tsx';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import TodayIcon from '@mui/icons-material/Today';
import GraphiqueIcon from '@mui/icons-material/BarChart';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TrophyIcon from '@mui/icons-material/EmojiEventsOutlined';
import ReposIcon from '@mui/icons-material/HotelOutlined';
import NoteIcon from '@mui/icons-material/StickyNote2Outlined';
import LienIcon from '@mui/icons-material/OpenInNew';
import EnvoiIcon from '@mui/icons-material/CloudUploadOutlined';
import EnvoyeIcon from '@mui/icons-material/CloudDoneOutlined';
import AthleteIcon from '@mui/icons-material/PersonOutline';
import ConditionIcon from '@mui/icons-material/Terrain';
import OrigineIcon from '@mui/icons-material/WarningAmber';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useVma } from '../Allures/useVma.ts';
import { useEtatSession } from '../useEtatSession.ts';
import { DansLaBarre } from '../BarrePage.tsx';
import { envoyerAutomatiquement, envoyerVersIntervalsIcu, texteIntervalsIcu } from './intervalsIcuApi.ts';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Chip from '@mui/material/Chip';
import { Link, useSearchParams } from 'react-router-dom';

import { formaterDuree, formaterDureeCourse } from '../Construction/formatDuree.ts';
import { afficherNotation, ajusterNotation, libelleAjustement, useAjustementRecup } from '../Construction/ajustementRecup.ts';
import { conditionsDe, normaliserAllures, type Condition } from '../Construction/seanceEngine.ts';
import { TYPES_SEANCE, typeEffectif, typeSeance, type TypeSeance } from '../Construction/classification.ts';
import { listerSeances, type Seance } from '../Construction/seancesApi.ts';
import { categorieDe, composerNotation, estPlanifiable, libelleUsage, modelePour, partiesModele, totauxAvecModele, usageDe, useModelesParDefaut } from '../Construction/composition.ts';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { couleurPourNom, LegendeZones, ZoneChip } from '../Allures/couleursZones.tsx';
import { zoneDepuisApi, type ZoneRow, type ZoneRowApi } from '../Construction/allureTable.ts';
import {
  actualiserSeance,
  deplacerSeance,
  libelleOrigine,
  listerPlanning,
  planifierSeance,
  planifierRepos,
  planifierNote,
  modifierNotes,
  retirerSeance,
  definirSemaine,
  infoSemaine,
  listerSemaines,
  TYPES_SEMAINE,
  type Semaine,
  type SeancePlanifiee,
  type TypeSemaine,
  listerDecomptes,
  poserDecompte,
  supprimerDecompte,
  type Decompte,
} from './planningApi.ts';
import { chevauchement, dateLocale, lundiDe, numeroSemaine, semainesEntre } from './decompte.ts';
import { genererPlan, telechargerPlan } from './exportPlan.ts';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import { ajouterJours, aujourdhuiDans, formaterKm, fuseauNavigateur, iso, JOURS, joursAffiches } from './dates.ts';
import DetailSeancePlanifiee from './DetailSeancePlanifiee.tsx';
import GraphiqueKilometrageDecompte from './GraphiqueKilometrageDecompte.tsx';
import { decompteAffiche } from './kilometrageDecompte.ts';
import { ecartUA } from './charge.ts';
import { estCoach, fuseauDuCalendrier, lireProfilAthlete, nomAthlete, useMoi, type AthleteRattache, type ProfilAthlete } from '../Auth/moi.ts';

// Types de donnees glissees (seance de la liste, ou seance deja planifiee).
const DND_SEANCE = 'application/x-runplan-seance';
const DND_PLANIFIEE = 'application/x-runplan-planifiee';
const DND_REPOS = 'application/x-runplan-repos';
const DND_NOTE = 'application/x-runplan-note';
// Note deja posee en cours de deplacement (libre sur tous les jours, comme une nouvelle note).
const DND_NOTE_POSEE = 'application/x-runplan-note-posee';

// Note mise par defaut sur chaque repos pose (modifiable ensuite au clic).
const NOTE_REPOS_DEFAUT = [
  'La journée de repos doit vous permettre de récupérer afin de repartir frais pour la suite de l\'entraînement.',
  'Ne vous privez pas, sans faire d\'excès. Évitez le stress, reposez-vous et détendez-vous.',
].join('\n');

const TOUTES_ALLURES = '__toutes__';
// Ecart d'UA tolere par le filtre "charge proche" (clic sur une seance posee).
const TOLERANCE_UA = 0.05;

type Tri = 'recentes' | 'ua-desc' | 'ua-asc' | 'nom';

// Largeur de la colonne "Mes seances" (px), reglable a la souris et
// memorisee dans le navigateur.
const LARGEUR_LISTE = { defaut: 300, min: 220, max: 600 };
const CLE_LARGEUR_LISTE = 'calendrier.largeurListe';

// Premier jour du mois <-> cle "AAAA-MM" stockee en session.
function cleMois(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function lireCleMois(cle: string): Date {
  const m = /^(\d{4})-(\d{2})$/.exec(cle);
  if (!m) {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, 1);
}

// Graphique du kilometrage du decompte affiche ou masque (memorise).
const CLE_GRAPHIQUE_DECOMPTE = 'calendrier.decompte.graphique';

function lireGraphiqueDecompte(): boolean {
  try {
    return localStorage.getItem(CLE_GRAPHIQUE_DECOMPTE) !== '0';
  } catch {
    return true;
  }
}

function sauverGraphiqueDecompte(v: boolean): void {
  try {
    localStorage.setItem(CLE_GRAPHIQUE_DECOMPTE, v ? '1' : '0');
  } catch {
    // stockage indisponible : le choix n'est pas memorise
  }
}

function lireLargeurListe(): number {
  try {
    const v = parseInt(localStorage.getItem(CLE_LARGEUR_LISTE) ?? '', 10);
    return isNaN(v) ? LARGEUR_LISTE.defaut : Math.min(LARGEUR_LISTE.max, Math.max(LARGEUR_LISTE.min, v));
  } catch {
    return LARGEUR_LISTE.defaut;
  }
}

function sauverLargeurListe(v: number): void {
  try {
    localStorage.setItem(CLE_LARGEUR_LISTE, String(v));
  } catch {
    // stockage indisponible : la largeur n'est simplement pas memorisee
  }
}

// Duree d'une seance ; a la seconde pour une competition de moins d'une heure.
function dureeAffichee(secondes: number, categorie?: string | null): string {
  return categorie === 'COMPETITION' ? formaterDureeCourse(secondes) : formaterDuree(secondes);
}


/**
 * Calendrier d'entrainement : les seances enregistrees (page Construction)
 * se glissent sur un jour pour les planifier ; une seance planifiee se
 * glisse sur un autre jour pour la deplacer, ou se retire avec la croix.
 */
export default function CalendrierSeances() {
  // Role : le coach etablit le calendrier (le sien ou celui d'un de ses
  // athletes, choisi en haut de page) ; l'athlete consulte le sien.
  const moi = useMoi();
  const lectureSeule = !estCoach(moi);
  const [params, setParams] = useSearchParams();
  const athletesConnus = (moi?.athletes ?? []).filter((a: AthleteRattache) => !!a.ssoUserId);
  const athleteChoisi: AthleteRattache | null = lectureSeule
    ? null
    : athletesConnus.find((a: AthleteRattache) => a.ssoUserId === params.get('athlete')) ?? null;
  const athlete: string | null = athleteChoisi?.ssoUserId ?? null;
  const choisirAthlete = (sso: string) => setParams(sso ? { athlete: sso } : {}, { replace: true });

  // Parametres de l'utilisateur, ou de l'athlete dont le coach etablit le
  // calendrier (VMA, allures, recuperations : textes intervals.icu, detail).
  const { table: tableMoi } = useAllureTable();
  const ajustementMoi = useAjustementRecup();
  // Le calendrier reste consultable sans VMA (pas de renvoi vers Allures).
  const [vmaMoi] = useVma({ exigee: false });
  const [profil, setProfil] = React.useState<{ vma: number; table: ZoneRow[]; ajustementRecup: number } | null>(null);
  React.useEffect(() => {
    setProfil(null);
    if (!athlete) return;
    lireProfilAthlete(athlete)
      .then((pr: ProfilAthlete) => setProfil({
        vma: pr.vma ?? 0,
        table: (pr.allures?.zones ?? []).map((z: unknown) => zoneDepuisApi(z as ZoneRowApi)),
        ajustementRecup: pr.ajustementRecup ?? 0,
      }))
      .catch((e: Error) => setMessage({ severity: 'error', texte: "Paramètres de l'athlète indisponibles : " + e.message }));
  }, [athlete]);
  const table: ZoneRow[] = profil && profil.table.length ? profil.table : tableMoi;
  // Notation affichee avec l'ajustement des recuperations (preference).
  const ajustementRecup: number = profil ? profil.ajustementRecup : ajustementMoi;
  // Envoi d'une seance vers intervals.icu : apercu du texte, puis envoi.
  const vma: number = profil ? profil.vma : vmaMoi;
  const [envoi, setEnvoi] = React.useState<{ p: SeancePlanifiee; texte?: string; erreur?: string } | null>(null);
  const [envoiEnCours, setEnvoiEnCours] = React.useState(false);
  const ouvrirEnvoi = (p: SeancePlanifiee) => setEnvoi({ p, ...texteIntervalsIcu(p, vma, table, ajustementRecup) });
  const envoyer = async () => {
    if (!envoi?.texte) return;
    setEnvoiEnCours(true);
    try {
      const maj = await envoyerVersIntervalsIcu(envoi.p.id, envoi.texte, athlete);
      setPlanning((actuel) => actuel.map((x) => (x.id === maj.id ? maj : x)));
      setMessage({ severity: 'success', texte: `« ${maj.nom} » envoyée vers intervals.icu.` });
      setEnvoi(null);
    } catch (err) {
      setEnvoi((e) => (e ? { ...e, erreur: (err as Error).message } : e));
    } finally {
      setEnvoiEnCours(false);
    }
  };
  // La seance a-t-elle des recuperations en temps modifiees par l'ajustement ?
  // Signale au coach seulement : l'athlete voit les seances ajustees sans
  // savoir qu'un ajustement existe.
  const recupPerso = React.useCallback(
    (notation: string) => !lectureSeule && ajustementRecup !== 0 && ajusterNotation(notation, ajustementRecup) !== notation,
    [ajustementRecup, lectureSeule],
  );
  // Indicateur "recuperation personnalisee" (icone chrono + ecart).
  const badgeRecup = (couleur?: string) => (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', whiteSpace: 'nowrap', color: couleur }}
    >
      <TimerOutlinedIcon sx={{ fontSize: 12 }} />
      {libelleAjustement(ajustementRecup)}
    </Box>
  );
  // Detail au survol : notation ajustee, et modele si different.
  const notationSurvol = (notation: string) =>
    recupPerso(notation) ? (
      <Box>
        <Box><code>{afficherNotation(notation, ajustementRecup)}</code></Box>
        <Box sx={{ mt: 0.5, opacity: 0.85 }}>
          Récupération personnalisée ({libelleAjustement(ajustementRecup)}) — modèle : <code>{normaliserAllures(notation)}</code>
        </Box>
      </Box>
    ) : (
      <code>{lectureSeule ? afficherNotation(notation, ajustementRecup) : normaliserAllures(notation)}</code>
    );
  // Conditions d'execution ("1'@5k[côte]") : libelles, et distance
  // approximative quand un effort se court en cote, descente ou trail.
  const libelleConditions = (notation: string) => conditionsDe(notation).map((c: Condition) => c.libelle.toLowerCase()).join(', ');
  const kmAffiches = (notation: string, distanceM?: number | null) => {
    const km = formaterKm(distanceM);
    return km && conditionsDe(notation).some((c: Condition) => c.sansAllure) ? `≈ ${km}` : km;
  };
  // Modele de seance applique aux corps de seance poses (preference).
  // Type d'entrainement d'une seance (liste) ou d'une seance posee : aucun
  // pour une competition ; pour un corps compose, celui du corps seul.
  const typeDe = React.useCallback(
    (x: { categorie?: string | null; typeSeance?: string | null; notation: string; notationCorps?: string | null; vma?: number | null }) =>
      x.categorie === 'COMPETITION' || x.categorie === 'REPOS' || x.categorie === 'NOTE' ? null : typeEffectif({ ...x, notation: x.notationCorps ?? x.notation }, table),
    [table],
  );
  // Modeles choisis pour l'athlete dont le coach etablit le calendrier.
  // Reserves au coach : l'athlete ne voit pas les modeles (409).
  const modeles = useModelesParDefaut(0, athlete, !lectureSeule);
  const aUnModele = !!modelePour(modeles, 'REPETITIONS');
  // Modele qui sera applique a une seance de la liste (corps de seance seulement).
  const modeleDe = (s: Seance) =>
    categorieDe(s) === 'CORPS' ? modelePour(modeles, usageDe(s.modeleSeance, typeEffectif(s, table))) : null;
  // Duree, distance et UA d'une seance de la liste une fois composee avec
  // son modele (meme somme que PlanningService.composer ; hors semaine
  // d'assimilation), ex : "Avec modèle : 1h05 · 13 km · 85 UA".
  const totalAvecModele = (s: Seance): string => {
    const totaux = totauxAvecModele(s, modeleDe(s), toutesSeances);
    if (!totaux) return '';
    return [
      totaux.dureeTotaleS ? formaterDuree(totaux.dureeTotaleS) : '',
      formaterKm(totaux.distanceTotaleM),
      totaux.chargeUA != null ? `${Math.round(totaux.chargeUA)} UA` : '',
    ].filter(Boolean).join(' · ');
  };
  // Apercu d'une seance de la liste telle qu'elle serait posee (composee
  // avec son modele), pour l'afficher dans le detail sans date.
  const apercuDe = (s: Seance): SeancePlanifiee => {
    const modele = modeleDe(s);
    const parties = partiesModele(modele, toutesSeances);
    const compose = !!(parties.echauffement || parties.recuperation);
    const totaux = compose ? totauxAvecModele(s, modele, toutesSeances) : null;
    return {
      id: '',
      idSeance: s.id,
      date: '',
      ordre: 0,
      nom: s.nom,
      notation: compose ? composerNotation(parties.echauffement?.notation, s.notation, parties.recuperation?.notation) : s.notation,
      dureeTotaleS: totaux ? totaux.dureeTotaleS : s.dureeTotaleS,
      distanceTotaleM: totaux ? totaux.distanceTotaleM : s.distanceTotaleM,
      chargeUA: totaux ? totaux.chargeUA : s.chargeUA,
      rpe: s.rpe,
      zonePrincipale: s.zonePrincipale,
      typeSeance: s.typeSeance,
      categorie: categorieDe(s),
      notes: s.notes,
      nomModele: compose ? modele?.nom : null,
      notationEchauffement: compose ? parties.echauffement?.notation : null,
      notationCorps: compose ? s.notation : null,
      notationRecuperation: compose ? parties.recuperation?.notation : null,
    };
  };
  // UA du corps de seance d'origine d'une seance posee avec un modele.
  const chargeCorps = (p: SeancePlanifiee): number | null => {
    if (!p.nomModele || !p.idSeance) return null;
    const corps = seances.find((s) => s.id === p.idSeance)?.chargeUA;
    return corps == null ? null : Math.round(corps);
  };
  // Colonne "Mes seances" redimensionnable (poignee entre la liste et le calendrier).
  const [largeurListe, setLargeurListe] = React.useState<number>(lireLargeurListe);
  const redimensionnement = React.useRef<{ x: number; largeur: number } | null>(null);
  const debutRedimensionnement = (e: React.PointerEvent<HTMLElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    redimensionnement.current = { x: e.clientX, largeur: largeurListe };
  };
  const redimensionner = (e: React.PointerEvent<HTMLElement>) => {
    const depart = redimensionnement.current;
    if (!depart) return;
    const v = Math.min(LARGEUR_LISTE.max, Math.max(LARGEUR_LISTE.min, depart.largeur + e.clientX - depart.x));
    setLargeurListe(v);
  };
  const finRedimensionnement = (e: React.PointerEvent<HTMLElement>) => {
    if (!redimensionnement.current) return;
    redimensionnement.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    sauverLargeurListe(largeurListe);
  };
  const largeurParDefaut = () => {
    setLargeurListe(LARGEUR_LISTE.defaut);
    sauverLargeurListe(LARGEUR_LISTE.defaut);
  };
  // Mois affiche, conserve en session ("AAAA-MM") pour retrouver sa position
  // en revenant sur la page.
  const [moisCle, setMoisCle] = useEtatSession('planning.mois', cleMois(new Date()));
  const mois = React.useMemo(() => lireCleMois(moisCle), [moisCle]);
  const setMois = (d: Date) => setMoisCle(cleMois(d));
  const [seances, setSeances] = React.useState<Seance[]>([]);
  // Toutes les seances, echauffements et recuperations compris (parties des modeles).
  const [toutesSeances, setToutesSeances] = React.useState<Seance[]>([]);
  const [recherche, setRecherche] = useEtatSession('planning.filtre.recherche', '');
  // Filtre par allure (zone principale) et tri de la liste des seances.
  const [allureFiltre, setAllureFiltre] = useEtatSession<string>('planning.filtre.allure', TOUTES_ALLURES);
  const [tri, setTri] = useEtatSession<Tri>('planning.filtre.tri', 'recentes');
  const [typeFiltre, setTypeFiltre] = useEtatSession<string>('planning.filtre.type', TOUTES_ALLURES);
  const [planning, setPlanning] = React.useState<SeancePlanifiee[]>([]);
  const [jourSurvole, setJourSurvole] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<{ severity: 'success' | 'warning' | 'error'; texte: string } | null>(null);

  const jours = React.useMemo<Date[]>(() => joursAffiches(mois), [mois]);
  const debut = iso(jours[0]);
  const fin = iso(jours[jours.length - 1]);
  // Jour courant de l'athlete (son fuseau horaire), meme si le coach est
  // ailleurs : les jours qui le precedent ne se modifient plus.
  const fuseau = fuseauDuCalendrier(moi, athleteChoisi);
  const aujourdhui = aujourdhuiDans(fuseau);

  // Couleur de la zone principale (cf Allures/couleursZones.tsx).
  const couleurZone = React.useCallback((nomZone?: string | null) => couleurPourNom(nomZone, table), [table]);

  React.useEffect(() => {
    // Seuls les corps de seance (composes avec le modele) et les seances
    // completes (posees telles quelles) se posent dans le calendrier : les
    // echauffements et recuperations y arrivent par le modele de seance.
    // Seances du coach (a glisser) ; l'athlete n'en a pas.
    if (lectureSeule) return;
    listerSeances()
      .then((liste: Seance[]) => {
        setToutesSeances(liste);
        setSeances(liste.filter((s: Seance) => estPlanifiable(categorieDe(s))));
      })
      .catch((e: Error) => setMessage({ severity: 'error', texte: 'Séances indisponibles : ' + e.message }));
  }, [lectureSeule]);

  // Semaines d'assimilation / d'affutage (lundi -> type).
  const [semaines, setSemaines] = React.useState<Map<string, TypeSemaine>>(new Map());

  const chargerPlanning = React.useCallback(() => {
    listerPlanning(debut, fin, athlete)
      .then(setPlanning)
      .catch((e: Error) => setMessage({ severity: 'error', texte: 'Calendrier indisponible : ' + e.message }));
    listerSemaines(debut, fin, athlete)
      .then((liste: Semaine[]) => setSemaines(new Map(liste.filter((x: Semaine) => x.type).map((x: Semaine): [string, TypeSemaine] => [x.lundi, x.type as TypeSemaine]))))
      .catch(() => { /* types de semaine indisponibles : semaines normales */ });
  }, [debut, fin, athlete]);

  // Seance recomposee deja envoyee : intervals.icu recoit le nouveau texte.
  const renvoyer = async (p: SeancePlanifiee): Promise<{ p: SeancePlanifiee; ok: boolean }> => {
    if (!p.dateEnvoiIcu) return { p, ok: true };
    const { texte } = texteIntervalsIcu(p, vma, table, ajustementRecup);
    if (!texte) return { p, ok: false };
    try {
      return { p: await envoyerVersIntervalsIcu(p.id, texte, athlete), ok: true };
    } catch {
      return { p, ok: false };
    }
  };

  // Choix du type d'une semaine (menu de la colonne Semaine).
  const [menuSemaine, setMenuSemaine] = React.useState<{ ancre: HTMLElement; lundi: string } | null>(null);
  const changerSemaine = async (lundi: string, type: TypeSemaine | null) => {
    setMenuSemaine(null);
    try {
      const resultat = await definirSemaine(lundi, type, athlete);
      setSemaines((m) => {
        const n = new Map(m);
        if (type) n.set(lundi, type); else n.delete(lundi);
        return n;
      });
      const recomposees: SeancePlanifiee[] = resultat.recomposees ?? [];
      const renvois = await Promise.all(recomposees.map(renvoyer));
      const parId = new Map(renvois.map((r) => [r.p.id, r.p]));
      setPlanning((actuel) => actuel.map((x) => parId.get(x.id) ?? x));
      const libelle = type ? `Semaine ${infoSemaine(type)?.libelle.toLowerCase()}` : 'Semaine normale';
      const nbIcu = renvois.filter((r) => r.p.dateEnvoiIcu).length;
      const echecs = renvois.filter((r) => !r.ok).length;
      setMessage({
        severity: echecs ? 'warning' : 'success',
        texte: `${libelle}${recomposees.length
          ? ` : ${recomposees.length} séance${recomposees.length > 1 ? 's' : ''} de répétitions recomposée${recomposees.length > 1 ? 's' : ''}`
            + `${nbIcu ? `, ${nbIcu - echecs}/${nbIcu} mise${nbIcu > 1 ? 's' : ''} à jour sur intervals.icu` : ''}`
          : '.'}`,
      });
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Type de semaine non enregistré : ' + (e as Error).message });
    }
  };

  React.useEffect(() => {
    chargerPlanning();
  }, [chargerPlanning]);

  // Decomptes de semaines (S13 ... S1) jusqu'a une competition ou une
  // semaine choisie (S1) ; debut donne par un nombre de semaines ou une date.
  // Plusieurs decomptes, sans chevauchement ; clic sur une pastille : retrait.
  const [decomptes, setDecomptes] = React.useState<Decompte[]>([]);
  React.useEffect(() => {
    setDecomptes([]);
    listerDecomptes(athlete)
      .then(setDecomptes)
      .catch(() => { /* decomptes indisponibles : pas de pastilles */ });
  }, [athlete]);
  const lundiCourant = iso(lundiDe(new Date()));
  const [edition, setEdition] = React.useState<
    { cible: string; origine: string; mode: 'nombre' | 'debut'; nombre: string; debut: string } | null
  >(null);
  // Ouvre la saisie pour une semaine cible : competition (nombre de semaines
  // propose) ou semaine choisie (date de debut proposee).
  const ouvrirDecompte = (date: string, origine: string, mode: 'nombre' | 'debut') => {
    const cible = iso(lundiDe(dateLocale(date)));
    setEdition({ cible, origine, mode, nombre: '12', debut: cible < lundiCourant ? cible : lundiCourant });
  };
  // Lundi de la premiere semaine (SN) saisie, ou null si la saisie est invalide.
  const debutSaisi = React.useMemo<string | null>(() => {
    if (!edition) return null;
    if (edition.mode === 'nombre') {
      const n = Number(edition.nombre);
      return Number.isInteger(n) && n >= 1 && n <= 104 ? iso(ajouterJours(dateLocale(edition.cible), -7 * (n - 1))) : null;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(edition.debut)) return null;
    const debut = iso(lundiDe(dateLocale(edition.debut)));
    return debut <= edition.cible && semainesEntre(debut, edition.cible) < 104 ? debut : null;
  }, [edition]);
  const conflit = edition && debutSaisi ? chevauchement(decomptes, debutSaisi, edition.cible) : null;
  const enregistrerDecompte = async () => {
    if (!edition || !debutSaisi || conflit) return;
    try {
      const d = await poserDecompte({ lundiCible: edition.cible, lundiDebut: debutSaisi }, athlete);
      setDecomptes((liste) => [...liste, d].sort((a, b) => a.lundiDebut.localeCompare(b.lundiDebut)));
      setEdition(null);
      setMessage({ severity: 'success', texte: 'Décompte enregistré.' });
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Décompte non enregistré : ' + (e as Error).message });
    }
  };
  const [aRetirer, setARetirer] = React.useState<Decompte | null>(null);
  const retirerDecompte = async () => {
    if (!aRetirer) return;
    try {
      await supprimerDecompte(aRetirer.id, athlete);
      setDecomptes((liste) => liste.filter((x) => x.id !== aRetirer.id));
      setMessage({ severity: 'success', texte: 'Décompte retiré.' });
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Décompte non retiré : ' + (e as Error).message });
    }
    setARetirer(null);
  };
  // Clic sur une pastille : export du plan (Markdown) ou retrait (coach).
  const [menuDecompte, setMenuDecompte] = React.useState<{ ancre: HTMLElement; decompte: Decompte } | null>(null);
  const exporterPlan = async (d: Decompte) => {
    setMenuDecompte(null);
    try {
      const markdown = await genererPlan(d, {
        athlete,
        nomAthlete: athleteChoisi ? nomAthlete(athleteChoisi) : moi?.nom || moi?.email || '',
        vma,
        table,
        ajuster: (n: string) => afficherNotation(n, ajustementRecup),
        libelleType: (x: SeancePlanifiee) => typeSeance(typeDe(x))?.libelle,
      });
      telechargerPlan(markdown, d);
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Plan non exporté : ' + (e as Error).message });
    }
  };
  const [graphiqueDecompte, setGraphiqueDecompte] = React.useState<boolean>(lireGraphiqueDecompte);
  const basculerGraphiqueDecompte = () => {
    setGraphiqueDecompte(!graphiqueDecompte);
    sauverGraphiqueDecompte(!graphiqueDecompte);
  };
  const decompteCharge = React.useMemo(() => decompteAffiche(decomptes, iso(lundiDe(dateLocale(aujourdhui)))), [decomptes, aujourdhui]);
  const formaterSemaine = (lundi: string) => dateLocale(lundi).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  const parJour = React.useMemo(() => {
    const m = new Map<string, SeancePlanifiee[]>();
    planning.forEach((p) => m.set(p.date, [...(m.get(p.date) ?? []), p]));
    m.forEach((liste) => liste.sort((a, b) => a.ordre - b.ordre));
    return m;
  }, [planning]);

  // Allures presentes parmi les seances, dans l'ordre des parametres d'allures.
  const allures = React.useMemo(() => {
    const presentes = new Set(seances.map((s) => s.zonePrincipale).filter((z): z is string => !!z));
    const ordonnees = table.map((z: ZoneRow) => z.nom).filter((nom: string) => presentes.has(nom));
    return [...ordonnees, ...Array.from(presentes).filter((nom) => !ordonnees.includes(nom))];
  }, [seances, table]);

  // Filtre par charge : clic sur une seance posee dans le calendrier, la
  // liste ne garde que les seances dont l'UA (composee avec le modele,
  // comme une fois posee) est a +/- TOLERANCE_UA de la sienne.
  const [filtreUA, setFiltreUA] = React.useState<{ id: string; nom: string; ua: number } | null>(null);
  const uaPosee = (s: Seance): number | null => totauxAvecModele(s, modeleDe(s), toutesSeances)?.chargeUA ?? s.chargeUA ?? null;
  const seancesFiltrees = React.useMemo(() => {
    const q = recherche.trim().toLowerCase();
    const procheUA = (s: Seance) => {
      if (!filtreUA) return true;
      const ua = uaPosee(s);
      return ua != null && Math.abs(ua - filtreUA.ua) <= filtreUA.ua * TOLERANCE_UA;
    };
    const filtrees = seances.filter(
      (s) =>
        (allureFiltre === TOUTES_ALLURES || s.zonePrincipale === allureFiltre)
        && (typeFiltre === TOUTES_ALLURES || typeDe(s) === typeFiltre)
        && (!q || `${s.nom} ${s.notation} ${s.zonePrincipale ?? ''}`.toLowerCase().includes(q))
        && procheUA(s),
    );
    // Seances sans UA toujours en fin de liste.
    const ua = (s: Seance, defaut: number) => (s.chargeUA ?? defaut);
    switch (tri) {
      case 'ua-desc':
        return [...filtrees].sort((a, b) => ua(b, -Infinity) - ua(a, -Infinity));
      case 'ua-asc':
        return [...filtrees].sort((a, b) => ua(a, Infinity) - ua(b, Infinity));
      case 'nom':
        return [...filtrees].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
      default:
        return filtrees; // ordre du serveur : modifiees le plus recemment en premier
    }
    // uaPosee depend des modeles et de toutes les seances.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seances, recherche, allureFiltre, typeFiltre, tri, typeDe, filtreUA, modeles, toutesSeances]);

  // Nouvelle seance (ou repos, note) posee : affichee, puis envoyee
  // automatiquement si le compte intervals.icu est configure.
  const ajouterAuPlanning = async (p: SeancePlanifiee) => {
    setPlanning((actuel) => [...actuel, p]);
    const envoi = await envoyerAutomatiquement(p, vma, table, ajustementRecup,
      athleteChoisi && athlete ? { athlete, icuConfigure: athleteChoisi.icuConfigure } : null);
    if (envoi.p !== p) setPlanning((actuel) => actuel.map((x) => (x.id === envoi.p.id ? envoi.p : x)));
    if (envoi.message) setMessage(envoi.message);
  };

  // Depot sur un jour : nouvelle planification, ou deplacement.
  // Les jours passes ne se modifient plus (aujourd'hui reste modifiable),
  // sauf pour les notes, qui se posent et se deplacent sur tous les jours.
  const deposer = async (e: React.DragEvent, date: string) => {
    if (lectureSeule || (date < aujourdhui && !glisseNote(e))) return;
    e.preventDefault();
    setJourSurvole(null);
    const idSeance = e.dataTransfer.getData(DND_SEANCE);
    const idPlanifiee = e.dataTransfer.getData(DND_PLANIFIEE);
    const repos = e.dataTransfer.types.includes(DND_REPOS);
    // Note : titre et consigne saisis avant de la poser.
    if (e.dataTransfer.types.includes(DND_NOTE)) {
      setNoteEditee({ p: null, date, nom: '', texte: '' });
      return;
    }
    try {
      if (repos || idSeance) {
        const p = repos ? await planifierRepos(date, NOTE_REPOS_DEFAUT, athlete) : await planifierSeance(idSeance, date, athlete);
        await ajouterAuPlanning(p);
      } else if (idPlanifiee) {
        // Deplacement immediat a l'ecran, puis enregistrement.
        setPlanning((actuel) => actuel.map((p) => (p.id === idPlanifiee ? { ...p, date, ordre: 999 } : p)));
        const avant = planning.find((x) => x.id === idPlanifiee);
        let p = await deplacerSeance(idPlanifiee, date, athlete);
        setPlanning((actuel) => actuel.map((x) => (x.id === p.id ? p : x)));
        // Changement de semaine d'assimilation / d'affutage : seance recomposee.
        if (p.recomposee) {
          const r = await renvoyer(p);
          p = r.p;
          setPlanning((actuel) => actuel.map((x) => (x.id === p.id ? p : x)));
          setMessage({
            severity: r.ok ? 'success' : 'warning',
            texte: `« ${p.nom} » recomposée avec le modèle de cette semaine${p.dateEnvoiIcu ? (r.ok ? ' et mise à jour sur intervals.icu' : ", mais intervals.icu n'a pas pu être mis à jour") : ''}.`,
          });
          return;
        }
        // Seance deja envoyee : intervals.icu est mis a jour par le serveur.
        if (p.avertissementIcu) {
          setMessage({ severity: 'warning', texte: p.avertissementIcu });
        } else if (p.dateEnvoiIcu && avant?.date !== date) {
          setMessage({ severity: 'success', texte: `« ${p.nom} » déplacée aussi sur intervals.icu.` });
        }
      }
    } catch (err) {
      setMessage({ severity: 'error', texte: 'Action impossible : ' + (err as Error).message });
      chargerPlanning();
    }
  };

  // Glissement d'une note (nouvelle ou deja posee) : libre sur tous les jours.
  const glisseNote = (e: React.DragEvent) =>
    e.dataTransfer.types.includes(DND_NOTE) || e.dataTransfer.types.includes(DND_NOTE_POSEE);

  const retirer = async (p: SeancePlanifiee) => {
    setPlanning((actuel) => actuel.filter((x) => x.id !== p.id));
    try {
      const avertissement = await retirerSeance(p.id, athlete);
      if (avertissement) {
        setMessage({ severity: 'warning', texte: avertissement });
      } else if (p.dateEnvoiIcu) {
        setMessage({ severity: 'success', texte: `« ${p.nom} » retirée aussi d'intervals.icu.` });
      }
    } catch (err) {
      setMessage({ severity: 'error', texte: 'Retrait impossible : ' + (err as Error).message });
      chargerPlanning();
    }
  };

  // Remet une seance posee a jour depuis sa seance d'origine (modifiee depuis la pose).
  const actualiser = async (avant: SeancePlanifiee) => {
    try {
      const r = await renvoyer(await actualiserSeance(avant.id, athlete));
      const p = r.p;
      setPlanning((actuel) => actuel.map((x) => (x.id === p.id ? p : x)));
      setMessage({
        severity: r.ok ? 'success' : 'warning',
        texte: `« ${p.nom} » mise à jour depuis la séance d'origine${p.dateEnvoiIcu ? (r.ok ? ' et sur intervals.icu' : ", mais intervals.icu n'a pas pu être mis à jour") : ''}.`,
      });
    } catch (err) {
      setMessage({ severity: 'error', texte: 'Mise à jour impossible : ' + (err as Error).message });
      chargerPlanning();
    }
  };

  // Detail d'une seance planifiee (clic sur la seance).
  const [detail, setDetail] = React.useState<SeancePlanifiee | null>(null);
  // Apercu d'une seance de la liste "Mes seances" (clic sur la seance).
  const [apercu, setApercu] = React.useState<Seance | null>(null);
  // Glisser-deposer en cours : les infobulles des seances posees sont
  // masquees pour ne pas cacher les jours ; au survol, elles attendent un
  // court arret avant de s'afficher.
  const [enGlissement, setEnGlissement] = React.useState(false);
  React.useEffect(() => {
    const debut = () => setEnGlissement(true);
    const fin = () => setEnGlissement(false);
    document.addEventListener('dragstart', debut);
    document.addEventListener('dragend', fin);
    document.addEventListener('drop', fin);
    return () => {
      document.removeEventListener('dragstart', debut);
      document.removeEventListener('dragend', fin);
      document.removeEventListener('drop', fin);
    };
  }, []);
  const infobulleSeancePosee = {
    enterDelay: 700,
    enterNextDelay: 700,
    disableInteractive: true,
    disableHoverListener: enGlissement,
    slotProps: { popper: { sx: enGlissement ? { display: 'none' } : undefined } },
  };

  // Note d'un repos, ou titre et consigne d'une note : edition dans une
  // petite fenetre (clic sur le repos / la note). p null : creation d'une
  // note a la date indiquee (depot de "Note" sur un jour).
  const [noteEditee, setNoteEditee] = React.useState<
    { p: SeancePlanifiee | null; date?: string; nom: string; texte: string } | null
  >(null);
  const editionNote = !!noteEditee && (noteEditee.p === null || noteEditee.p.categorie === 'NOTE');
  const enregistrerNote = async () => {
    if (!noteEditee) return;
    const { p, date, nom, texte } = noteEditee;
    setNoteEditee(null);
    try {
      if (!p) {
        if (date) await ajouterAuPlanning(await planifierNote(date, nom, texte, athlete));
        return;
      }
      const maj = await modifierNotes(p.id, texte, athlete, p.categorie === 'NOTE' ? nom : undefined);
      setPlanning((actuel) => actuel.map((x) => (x.id === maj.id ? maj : x)));
      if (maj.avertissementIcu) setMessage({ severity: 'warning', texte: maj.avertissementIcu });
    } catch (err) {
      setMessage({ severity: 'error', texte: 'Note non enregistrée : ' + (err as Error).message });
    }
  };

  // Une note (categorie NOTE) se modifie, se deplace et se retire sur tous les jours.
  const noteLibre = (p: SeancePlanifiee) => !lectureSeule && p.categorie === 'NOTE';

  const totalSemaine = (semaine: Date[]) => {
    let duree = 0;
    let distance = 0;
    let charge = 0;
    // Repartition des seances de la semaine par type (ex : Long x2).
    const parType = new Map<string, number>();
    let nbCompetitions = 0;
    let nbRepos = 0;
    semaine.forEach((j) =>
      (parJour.get(iso(j)) ?? []).forEach((p) => {
        duree += p.dureeTotaleS ?? 0;
        distance += p.distanceTotaleM ?? 0;
        charge += Math.round(p.chargeUA ?? 0);
        const code = typeDe(p);
        if (p.categorie === 'COMPETITION') nbCompetitions += 1;
        if (p.categorie === 'REPOS') nbRepos += 1;
        if (code) parType.set(code, (parType.get(code) ?? 0) + 1);
      }),
    );
    const repartition = TYPES_SEANCE
      .filter((t: TypeSeance) => parType.has(t.code))
      .map((t: TypeSeance) => `${t.abrege}${(parType.get(t.code) ?? 0) > 1 ? ` ×${parType.get(t.code)}` : ''}`);
    if (nbCompetitions > 0) repartition.push(`Compétition${nbCompetitions > 1 ? ` ×${nbCompetitions}` : ''}`);
    if (nbRepos > 0) repartition.push(`Repos${nbRepos > 1 ? ` ×${nbRepos}` : ''}`);
    return { duree, distance, charge, repartition };
  };

  const titreMois = mois.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <Box component="main" sx={{ p: { xs: 2, md: 12 }, pt: { xs: 9, md: 10 } }}>
      {/* Coach : son calendrier ou celui d'un athlete rattache, choisi dans
          la barre de menu (a cote du titre de la page) */}
      {!lectureSeule && athletesConnus.length > 0 && (
        <DansLaBarre>
          <Tooltip title="Calendrier de" placement="bottom">
            <TextField
              select
              size="small"
              variant="standard"
              value={athlete ?? ''}
              onChange={(e) => choisirAthlete(e.target.value)}
              inputProps={{ 'aria-label': 'Calendrier de' }}
              SelectProps={{ displayEmpty: true }}
              InputProps={{
                disableUnderline: true,
                // Athlete choisi : sa photo (dans la valeur) remplace l'icone.
                startAdornment: athlete ? undefined : <InputAdornment position="start"><AthleteIcon sx={{ color: '#fff' }} /></InputAdornment>,
              }}
              sx={{
                minWidth: 160,
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 1,
                px: 1,
                '& .MuiInputBase-root, & .MuiSvgIcon-root': { color: '#fff' },
              }}
            >
              <MenuItem value="">Moi</MenuItem>
              {athletesConnus.map((a: AthleteRattache) => (
                <MenuItem key={a.id} value={a.ssoUserId ?? ''}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <AvatarAthlete athlete={a} taille={22} />
                    {nomAthlete(a)}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
        </DansLaBarre>
      )}
      {lectureSeule && moi && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ton calendrier est établi par {moi.coach ? `ton coach (${moi.coach})` : 'ton coach'} : clique sur une séance pour en voir le détail.
        </Alert>
      )}
      {/* Athlete dont le coach etablit le calendrier : sa photo, et sa fiche (page Athletes) */}
      {athleteChoisi && (
        <Alert
          severity="info"
          icon={<AvatarAthlete athlete={athleteChoisi} taille={40} />}
          sx={{ mb: 2, alignItems: 'center' }}
          action={
            <Button color="inherit" size="small" component={Link} to={`/athletes/${encodeURIComponent(athleteChoisi.id)}`}>
              Voir sa fiche
            </Button>
          }
        >
          Tu établis le calendrier de{' '}
          <Link to={`/athletes/${encodeURIComponent(athleteChoisi.id)}`}><b>{nomAthlete(athleteChoisi)}</b></Link> avec tes séances et{' '}
          <Link to={`/athletes/${encodeURIComponent(athleteChoisi.id)}?onglet=modeles`}>ses modèles de séance</Link> ; les allures
          sont calculées avec sa VMA{profil?.vma ? ` (${profil.vma.toLocaleString('fr-FR')} km/h)` : ''}.
          {athleteChoisi.icuConfigure ? ' Les séances ajoutées sont envoyées sur son intervals.icu.' : " Son compte intervals.icu n'est pas relié."}
          {' '}Son calendrier suit son fuseau horaire, <b>{fuseau}</b>
          {athleteChoisi.fuseauRenseigne ? '' : ' (non renseigné, fuseau par défaut tant qu\u2019il ne s\u2019est pas reconnecté)'}
          {fuseau !== fuseauNavigateur() ? ', différent du tien' : ''} : on y est le{' '}
          {new Date(aujourdhui + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })},
          {' '}les jours précédents ne se modifient plus.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: { xs: 3, lg: 0 }, alignItems: { xs: 'flex-start', lg: 'stretch' }, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Coach : seances enregistrees, a glisser sur le calendrier */}
        {!lectureSeule && (
          <>
        {/* Seances enregistrees, a glisser sur le calendrier */}
        <Paper variant="outlined" sx={{ width: { xs: '100%', lg: largeurListe }, flexShrink: 0, p: 2, position: { lg: 'sticky' }, top: 80, alignSelf: 'flex-start' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Mes séances</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Glisse une séance sur un jour pour la planifier
            {aUnModele ? <> : les corps de séance y sont composés avec ton modèle de répétitions ou de sortie continue, les séances « complètes » sont posées telles quelles.</> : '.'}
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Rechercher…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <TextField
              select
              size="small"
              label="Allure"
              value={allureFiltre}
              onChange={(e) => setAllureFiltre(e.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <MenuItem value={TOUTES_ALLURES}>Toutes</MenuItem>
              {allures.map((nom) => (
                <MenuItem key={nom} value={nom}>
                  <ZoneChip nom={nom} table={table} />
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Trier par"
              value={tri}
              onChange={(e) => setTri(e.target.value as Tri)}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <MenuItem value="recentes">Plus récentes</MenuItem>
              <MenuItem value="ua-desc">UA décroissante</MenuItem>
              <MenuItem value="ua-asc">UA croissante</MenuItem>
              <MenuItem value="nom">Nom</MenuItem>
            </TextField>
          </Box>
          <TextField
            select
            size="small"
            fullWidth
            label="Type de séance"
            value={typeFiltre}
            onChange={(e) => setTypeFiltre(e.target.value)}
            sx={{ mb: 1.5 }}
          >
            <MenuItem value={TOUTES_ALLURES}>Tous</MenuItem>
            {TYPES_SEANCE.map((t: TypeSeance) => <MenuItem key={t.code} value={t.code}>{t.libelle}</MenuItem>)}
          </TextField>
          {filtreUA && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`UA ${Math.round(filtreUA.ua)} ± ${Math.round(TOLERANCE_UA * 100)} % · ${normaliserAllures(filtreUA.nom)}`}
              title="Séances de charge proche de la séance cliquée dans le calendrier"
              onDelete={() => setFiltreUA(null)}
              sx={{ mb: 1, maxWidth: '100%' }}
            />
          )}
          {(allureFiltre !== TOUTES_ALLURES || typeFiltre !== TOUTES_ALLURES || recherche.trim() || filtreUA) && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              {seancesFiltrees.length} séance(s) sur {seances.length}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '65vh', overflowY: 'auto', pr: 0.5 }}>
            {/* Repos : toujours disponible, se pose comme une seance */}
            <Tooltip title="Glisse sur un jour pour poser un repos ; clic sur le repos posé pour ajouter une note" placement="right" arrow>
              <Paper
                variant="outlined"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(DND_REPOS, 'repos');
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                sx={{
                  p: 1,
                  cursor: 'grab',
                  borderLeft: 4,
                  borderLeftColor: 'grey.500',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <ReposIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Repos</Typography>
              </Paper>
            </Tooltip>
            {/* Note : titre + consigne pour la journee, envoyee a intervals.icu */}
            <Tooltip title="Glisse sur un jour pour poser une note (consigne) ; clic sur la note posée pour la modifier" placement="right" arrow>
              <Paper
                variant="outlined"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(DND_NOTE, 'note');
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                sx={{
                  p: 1,
                  cursor: 'grab',
                  borderLeft: 4,
                  borderLeftColor: 'warning.main',
                  bgcolor: 'var(--fond-note)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <NoteIcon fontSize="small" sx={{ color: 'warning.dark' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Note</Typography>
              </Paper>
            </Tooltip>
            {seances.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Aucune séance : crée-en sur la page <Link to="/seances">Séances</Link>.
              </Typography>
            )}
            {seancesFiltrees.map((s) => (
              <Tooltip
                key={s.id}
                title={
                  <Box>
                    {notationSurvol(s.notation)}
                    {modeleDe(s) && (
                      <Box sx={{ mt: 0.5, opacity: 0.85 }}>
                        Modèle « {modeleDe(s)?.nom} » ({libelleUsage(usageDe(s.modeleSeance, typeEffectif(s, table)))})
                      </Box>
                    )}
                    <Box sx={{ mt: 0.5, opacity: 0.85 }}>Clic : détail de la séance</Box>
                  </Box>
                }
                placement="right"
                arrow
              >
                <Paper
                  variant="outlined"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(DND_SEANCE, s.id ?? '');
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  onClick={() => setApercu(s)}
                  sx={{
                    p: 1,
                    cursor: 'grab',
                    borderLeft: 4,
                    borderLeftColor: couleurZone(s.zonePrincipale),
                    '&:hover': { boxShadow: 2 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, minWidth: 0 }} noWrap>{normaliserAllures(s.nom)}</Typography>
                    {s.portee === 'SOCLE' && <Chip size="small" color="info" variant="outlined" label="Socle" sx={{ height: 18, fontSize: '0.7rem', flexShrink: 0 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {categorieDe(s) === 'COMPETITION' && <TrophyIcon sx={{ fontSize: 13, verticalAlign: 'text-bottom', mr: 0.5 }} />}
                    {[categorieDe(s) === 'SEANCE' ? 'Séance' : '', categorieDe(s) === 'COMPETITION' ? 'Compétition' : '', typeSeance(typeDe(s))?.abrege, s.zonePrincipale, s.dureeTotaleS ? dureeAffichee(s.dureeTotaleS, categorieDe(s)) : '', kmAffiches(s.notation, s.distanceTotaleM)]
                      .filter(Boolean)
                      .join(' · ')}
                    {recupPerso(s.notation) && <> · {badgeRecup('secondary.main')}</>}
                  </Typography>
                  {/* Charge (UA) et RPE, comme sur les seances posees dans le calendrier */}
                  {(s.chargeUA || s.rpe) ? (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {[s.chargeUA ? `${Math.round(s.chargeUA)} UA` : '', s.rpe ? `RPE ${s.rpe}` : '']
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  ) : null}
                  {/* Totaux une fois posee avec le modele, pour comparer avec le calendrier */}
                  {totalAvecModele(s) && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      Avec modèle : {totalAvecModele(s)}
                    </Typography>
                  )}
                </Paper>
              </Tooltip>
            ))}
          </Box>
        </Paper>

        {/* Poignee de redimensionnement de la liste (ecrans larges) */}
        <Tooltip title="Glisse pour ajuster la largeur — double-clic : largeur par défaut" placement="top">
          <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Largeur de la liste des séances"
            onPointerDown={debutRedimensionnement}
            onPointerMove={redimensionner}
            onPointerUp={finRedimensionnement}
            onPointerCancel={finRedimensionnement}
            onDoubleClick={largeurParDefaut}
            sx={{
              display: { xs: 'none', lg: 'flex' },
              width: 24,
              flexShrink: 0,
              cursor: 'col-resize',
              justifyContent: 'center',
              touchAction: 'none',
              '&::after': { content: '""', width: 2, borderRadius: 1, bgcolor: 'divider', transition: 'background-color .15s' },
              '&:hover::after, &:active::after': { bgcolor: 'primary.main' },
            }}
          />
        </Tooltip>
          </>
        )}

        {/* Calendrier du mois */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <IconButton aria-label="Mois précédent" onClick={() => setMois(new Date(mois.getFullYear(), mois.getMonth() - 1, 1))}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 190, textAlign: 'center', textTransform: 'capitalize' }}>
              {titreMois}
            </Typography>
            <IconButton aria-label="Mois suivant" onClick={() => setMois(new Date(mois.getFullYear(), mois.getMonth() + 1, 1))}>
              <ChevronRightIcon />
            </IconButton>
            <Button
              size="small"
              startIcon={<TodayIcon />}
              onClick={() => {
                const d = new Date();
                setMois(new Date(d.getFullYear(), d.getMonth(), 1));
              }}
            >
              Aujourd'hui
            </Button>
            {decompteCharge && (
              <Tooltip title={graphiqueDecompte ? 'Masquer le graphique du kilométrage du décompte' : 'Afficher le graphique du kilométrage du décompte'}>
                <Chip
                  size="small"
                  icon={<GraphiqueIcon />}
                  label="Kilométrage"
                  color={graphiqueDecompte ? 'primary' : 'default'}
                  variant={graphiqueDecompte ? 'filled' : 'outlined'}
                  onClick={basculerGraphiqueDecompte}
                  aria-pressed={graphiqueDecompte}
                />
              </Tooltip>
            )}
            {!lectureSeule && (
            <Tooltip
              title={aUnModele
                ? `Échauffement et récupération ajoutés aux corps de séance posés — répétitions : ${modeles.repetitions?.nom ?? '(modèle continu)'}, sortie continue : ${modeles.continu?.nom ?? '(modèle répétitions)'} (modifiable dans Mes préférences)`
                : 'Les séances sont posées seules : choisis tes modèles de séance dans Mes préférences'}
            >
              <Chip
                size="small"
                variant="outlined"
                color={aUnModele ? 'primary' : 'default'}
                label={aUnModele
                  ? `Modèles : ${[modeles.repetitions?.nom, modeles.continu?.nom].filter(Boolean).join(' / ')}`
                  : 'Aucun modèle de séance'}
                component={Link}
                to={athleteChoisi ? `/athletes/${encodeURIComponent(athleteChoisi.id)}?onglet=modeles` : '/preferences?onglet=modeles'}
                clickable
                sx={{ ml: 'auto' }}
              />
            </Tooltip>
            )}
            {!lectureSeule && ajustementRecup !== 0 && (
              <Tooltip title="Ajustement appliqué à chaque récupération en temps de tes séances (modifiable dans Mes préférences)">
                <Chip
                  size="small"
                  color="secondary"
                  variant="outlined"
                  icon={<TimerOutlinedIcon />}
                  label={`Récupérations personnalisées : ${libelleAjustement(ajustementRecup)}`}
                  component={Link}
                  to="/preferences?onglet=recuperations"
                  clickable
                />
              </Tooltip>
            )}
          </Box>

          {/* Kilometrage prevu par semaine du decompte en cours (ou du prochain) */}
          {decompteCharge && graphiqueDecompte && (
            <GraphiqueKilometrageDecompte
              decompte={decompteCharge}
              athlete={athlete}
              aujourdhui={aujourdhui}
              lundiCourant={iso(lundiDe(dateLocale(aujourdhui)))}
              planning={planning}
              debut={debut}
              fin={fin}
              semaines={semaines}
            />
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr)) 110px', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {[...JOURS, 'Semaine'].map((j) => (
              <Box key={j} sx={{ p: 1, bgcolor: 'action.hover', fontWeight: 600, fontSize: '0.8rem', textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                {j}
              </Box>
            ))}

            {Array.from({ length: 6 }, (_, s) => jours.slice(s * 7, s * 7 + 7)).map((semaine, s) => {
              const total = totalSemaine(semaine);
              const lundiSemaine = iso(semaine[0]);
              const typeSemaine = infoSemaine(semaines.get(lundiSemaine));
              const numero = numeroSemaine(decomptes, lundiSemaine);
              return (
                <React.Fragment key={s}>
                  {semaine.map((jour) => {
                    const date = iso(jour);
                    // Les jours de la semaine en cours ne sont jamais grises.
                    const horsMois = jour.getMonth() !== mois.getMonth() && iso(lundiDe(jour)) !== lundiCourant;
                    const survole = jourSurvole === date;
                    // Jour passe : rien ne s'y pose, ne s'y deplace ou n'en part (sauf une note).
                    const modifiable = !lectureSeule && date >= aujourdhui;
                    return (
                      <Box
                        key={date}
                        onDragOver={(e) => {
                          if (!modifiable && (lectureSeule || !glisseNote(e))) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = e.dataTransfer.types.includes(DND_PLANIFIEE) ? 'move' : 'copy';
                          if (jourSurvole !== date) setJourSurvole(date);
                        }}
                        onDragLeave={() => setJourSurvole((j) => (j === date ? null : j))}
                        onDrop={(e) => deposer(e, date)}
                        sx={{
                          minHeight: 110,
                          p: 0.5,
                          borderRight: 1,
                          borderBottom: s < 5 ? 1 : 0,
                          borderColor: 'divider',
                          bgcolor: survole ? 'action.selected' : typeSemaine ? typeSemaine.fond : horsMois ? 'action.hover' : 'background.paper',
                          outline: survole ? '2px dashed' : 'none',
                          outlineColor: 'primary.main',
                          outlineOffset: -3,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            alignSelf: 'flex-end',
                            fontWeight: date === aujourdhui ? 700 : 400,
                            color: horsMois ? 'text.disabled' : date === aujourdhui ? 'primary.contrastText' : 'text.secondary',
                            bgcolor: date === aujourdhui ? 'primary.main' : 'transparent',
                            borderRadius: '50%',
                            width: 22,
                            height: 22,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {jour.getDate()}
                        </Typography>
                        {(parJour.get(date) ?? []).map((p) => (p.categorie === 'REPOS' || p.categorie === 'NOTE' ? (
                          <Tooltip
                            key={p.id}
                            arrow
                            {...infobulleSeancePosee}
                            title={
                              <Box>
                                <b>{p.categorie === 'NOTE' ? p.nom : 'Repos'}</b>
                                {p.notes && <Box sx={{ whiteSpace: 'pre-line' }}>{p.notes}</Box>}
                                {(modifiable || noteLibre(p)) && (
                                  <Box sx={{ mt: 0.5, opacity: 0.85 }}>
                                    {p.categorie === 'NOTE' ? 'Clic pour modifier la note' : `Clic pour ${p.notes ? 'modifier' : 'ajouter'} une note`}
                                  </Box>
                                )}
                              </Box>
                            }
                          >
                            <Box
                              draggable={modifiable || noteLibre(p)}
                              onDragStart={(e) => {
                                e.dataTransfer.setData(DND_PLANIFIEE, p.id);
                                if (p.categorie === 'NOTE') e.dataTransfer.setData(DND_NOTE_POSEE, p.id);
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onClick={() => (modifiable || noteLibre(p)) && setNoteEditee({ p, nom: p.nom, texte: p.notes ?? '' })}
                              sx={{
                                position: 'relative',
                                cursor: modifiable || noteLibre(p) ? 'pointer' : 'default',
                                fontSize: '0.75rem',
                                lineHeight: 1.25,
                                p: '3px 18px 3px 6px',
                                borderRadius: 0.75,
                                color: 'text.secondary',
                                bgcolor: p.categorie === 'NOTE' ? 'var(--fond-note)' : 'action.selected',
                                border: 1,
                                borderColor: p.categorie === 'NOTE' ? 'warning.light' : 'divider',
                                opacity: horsMois ? 0.6 : 1,
                                '&:hover .retirer': { opacity: 1 },
                              }}
                            >
                              <Box sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {p.categorie === 'NOTE' ? <NoteIcon sx={{ fontSize: 14 }} /> : <ReposIcon sx={{ fontSize: 14 }} />}
                                {p.categorie === 'NOTE' ? p.nom : 'Repos'}
                                {p.dateEnvoiIcu && <EnvoyeIcon titleAccess="Envoyé vers intervals.icu" sx={{ fontSize: 13 }} />}
                              </Box>
                              {p.notes && (
                                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                                  {p.notes}
                                </Box>
                              )}
                              <IconButton
                                className="retirer"
                                size="small"
                                aria-label="Envoyer vers intervals.icu"
                                title="Envoyer vers intervals.icu"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  ouvrirEnvoi(p);
                                }}
                                sx={{ position: 'absolute', top: 0, right: 16, p: '2px', color: 'inherit', opacity: 0 }}
                              >
                                <EnvoiIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              {(modifiable || noteLibre(p)) && (
                              <IconButton
                                className="retirer"
                                size="small"
                                aria-label="Retirer du calendrier"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  retirer(p);
                                }}
                                sx={{ position: 'absolute', top: 0, right: 0, p: '2px', color: 'inherit', opacity: 0 }}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              )}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            key={p.id}
                            arrow
                            {...infobulleSeancePosee}
                            title={
                              <Box>
                                <b>{normaliserAllures(p.nom)}</b>
                                {p.categorie === 'COMPETITION' && <Box>Compétition</Box>}
                                {typeSeance(typeDe(p)) && <Box>{typeSeance(typeDe(p))?.libelle}</Box>}
                                {libelleConditions(p.notation) && <Box>Conditions : {libelleConditions(p.notation)}</Box>}
                                {p.nomModele ? (
                                  <Box sx={{ my: 0.5 }}>
                                    <Box>Modèle « {p.nomModele} » :</Box>
                                    {p.notationEchauffement && <Box>Échauffement : {notationSurvol(p.notationEchauffement)}</Box>}
                                    <Box>Corps : {notationSurvol(p.notationCorps ?? p.notation)}</Box>
                                    {p.notationRecuperation && <Box>Récupération : {notationSurvol(p.notationRecuperation)}</Box>}
                                  </Box>
                                ) : (
                                  <Box>{notationSurvol(p.notation)}</Box>
                                )}
                                <Box>
                                  {[p.zonePrincipale, p.dureeTotaleS ? dureeAffichee(p.dureeTotaleS, p.categorie) : '', kmAffiches(p.notation, p.distanceTotaleM),
                                    p.chargeUA ? `${Math.round(p.chargeUA)} UA` : '', p.rpe ? `RPE ${p.rpe}` : '']
                                    .filter(Boolean).join(' · ')}
                                </Box>
                                {p.chargeUA != null && chargeCorps(p) != null && (
                                  <Box>Charge : {Math.round(p.chargeUA)} UA avec modèle, {chargeCorps(p)} UA corps seul ({ecartUA(p.chargeUA, chargeCorps(p))} UA)</Box>
                                )}
                                {p.notes && <Box sx={{ mt: 0.5, fontStyle: 'italic', whiteSpace: 'pre-line' }}>{p.notes}</Box>}
                                {p.lienCourse && <Box sx={{ mt: 0.5 }}>Clic sur ↗ : site de la course</Box>}
                                {libelleOrigine(p.etatOrigine) && <Box sx={{ mt: 0.5, fontWeight: 600 }}>⚠ {libelleOrigine(p.etatOrigine)}</Box>}
                                <Box sx={{ mt: 0.5, opacity: 0.85 }}>
                                  {p.chargeUA ? 'Clic : séances de charge proche dans la liste · ' : ''}Double clic : détail et visualisation de la séance
                                </Box>
                              </Box>
                            }
                          >
                            <Box
                              draggable={modifiable}
                              onDragStart={(e) => {
                                e.dataTransfer.setData(DND_PLANIFIEE, p.id);
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onClick={() => {
                                if (!p.chargeUA) return;
                                setFiltreUA({ id: p.id, nom: p.nom, ua: p.chargeUA });
                                setTri('ua-asc');
                              }}
                              onDoubleClick={() => setDetail(p)}
                              sx={{
                                position: 'relative',
                                cursor: 'pointer',
                                userSelect: 'none',
                                outline: (t) => (filtreUA?.id === p.id ? `2px solid ${t.palette.primary.main}` : 'none'),
                                outlineOffset: 1,
                                fontSize: '0.75rem',
                                lineHeight: 1.25,
                                p: '3px 18px 3px 6px',
                                borderRadius: 0.75,
                                color: '#fff',
                                bgcolor: couleurZone(p.zonePrincipale),
                                opacity: horsMois ? 0.6 : 1,
                                '&:hover .retirer': { opacity: 1 },
                              }}
                            >
                              <Box sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.categorie === 'COMPETITION' && (
                                  <TrophyIcon sx={{ fontSize: 13, verticalAlign: 'text-bottom', mr: 0.25 }} />
                                )}
                                {normaliserAllures(p.nom)}
                                {libelleConditions(p.notation) && (
                                  <ConditionIcon titleAccess={`Conditions : ${libelleConditions(p.notation)}`} sx={{ fontSize: 13, verticalAlign: 'text-bottom', ml: 0.5 }} />
                                )}
                                {p.dateEnvoiIcu && (
                                  <EnvoyeIcon titleAccess="Envoyée vers intervals.icu" sx={{ fontSize: 13, verticalAlign: 'text-bottom', ml: 0.5 }} />
                                )}
                                {p.etatOrigine && (
                                  <OrigineIcon titleAccess={libelleOrigine(p.etatOrigine) ?? undefined} sx={{ fontSize: 13, verticalAlign: 'text-bottom', ml: 0.5, color: '#ffe082' }} />
                                )}
                              </Box>
                              {/* Site de la course (competition posee depuis l'Agenda) */}
                              {p.lienCourse && (
                                <IconButton
                                  component="a"
                                  href={p.lienCourse}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  aria-label="Site de la course"
                                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                  draggable={false}
                                  sx={{ position: 'absolute', bottom: 0, right: 0, p: '2px', color: 'inherit' }}
                                >
                                  <LienIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              )}
                              {/* Duree et kilometrage total de la seance */}
                              {(p.dureeTotaleS || p.distanceTotaleM) ? (
                                <Box sx={{ opacity: 0.9, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                  {[p.dureeTotaleS ? dureeAffichee(p.dureeTotaleS, p.categorie) : '', kmAffiches(p.notation, p.distanceTotaleM)]
                                    .filter(Boolean)
                                    .join(' · ')}
                                  {recupPerso(p.notation) && <> · {badgeRecup()}</>}
                                </Box>
                              ) : null}
                              {/* Charge (UA) et RPE de la seance */}
                              {(p.chargeUA || p.rpe) ? (
                                <Box sx={{ opacity: 0.9, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                  {[p.chargeUA ? `${Math.round(p.chargeUA)} UA` : '', chargeCorps(p) != null ? `corps ${chargeCorps(p)}` : '', p.rpe ? `RPE ${p.rpe}` : '']
                                    .filter(Boolean)
                                    .join(' · ')}
                                </Box>
                              ) : null}
                              <IconButton
                                className="retirer"
                                size="small"
                                aria-label="Envoyer vers intervals.icu"
                                title="Envoyer vers intervals.icu"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  ouvrirEnvoi(p);
                                }}
                                sx={{ position: 'absolute', top: 0, right: 16, p: '2px', color: 'inherit', opacity: 0 }}
                              >
                                <EnvoiIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              {modifiable && (
                              <IconButton
                                className="retirer"
                                size="small"
                                aria-label="Retirer du calendrier"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  retirer(p);
                                }}
                                sx={{ position: 'absolute', top: 0, right: 0, p: '2px', color: 'inherit', opacity: 0 }}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              )}
                            </Box>
                          </Tooltip>
                        )))}
                      </Box>
                    );
                  })}
                  <Box sx={{ p: 1, borderBottom: s < 5 ? 1 : 0, borderColor: 'divider', bgcolor: typeSemaine ? typeSemaine.fond : 'action.hover', borderLeft: typeSemaine ? `3px solid ${typeSemaine.couleur}` : 'none', fontSize: '0.75rem', color: 'text.secondary', display: 'flex', flexDirection: 'column', gap: 0.25, justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 0.5 }}>
                    {/* Pastille du decompte (S13 ... S1), couleur du type de semaine */}
                    {numero && (
                      <Tooltip title={`S${numero.n} : semaine ${typeSemaine ? `d'${typeSemaine.libelle.toLowerCase()}` : 'normale'}, S1 la semaine du ${formaterSemaine(numero.decompte.lundiCible)}. Clic pour exporter le plan${lectureSeule ? '' : ' ou retirer ce décompte'}.`}>
                        <Chip
                          size="small"
                          variant={typeSemaine ? 'filled' : 'outlined'}
                          label={`S${numero.n}`}
                          onClick={(e) => setMenuDecompte({ ancre: e.currentTarget, decompte: numero.decompte })}
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            ...(typeSemaine ? { bgcolor: typeSemaine.couleur, color: '#fff' } : { color: 'text.primary' }),
                          }}
                        />
                      </Tooltip>
                    )}
                    {/* Type de semaine : normale, assimilation ou affutage (clic pour changer) */}
                    {(!lectureSeule || typeSemaine) && (
                    <Tooltip title={lectureSeule
                      ? typeSemaine?.description ?? ''
                      : typeSemaine ? `${typeSemaine.description} : les répétitions prennent ton modèle d'assimilation / d'affûtage. Clic pour changer.` : "Marquer comme semaine d'assimilation ou d'affûtage"}>
                      <Chip
                        size="small"
                        variant={typeSemaine ? 'filled' : 'outlined'}
                        label={typeSemaine ? typeSemaine.libelle : 'Normale'}
                        onClick={lectureSeule ? undefined : (e) => setMenuSemaine({ ancre: e.currentTarget, lundi: lundiSemaine })}
                        sx={{
                          alignSelf: 'flex-start',
                          height: 20,
                          fontSize: '0.68rem',
                          mb: 0.5,
                          ...(typeSemaine ? { bgcolor: typeSemaine.couleur, color: '#fff' } : { color: 'text.secondary' }),
                        }}
                      />
                    </Tooltip>
                    )}
                    </Box>
                    {total.duree > 0 || total.repartition.length > 0 ? (
                      <>
                        {total.duree > 0 && (
                          <>
                            <Box sx={{ fontWeight: 600, color: 'text.primary' }}>{formaterDuree(total.duree)}</Box>
                            <Box>{formaterKm(total.distance)}</Box>
                            <Box>{Math.round(total.charge)} UA</Box>
                          </>
                        )}
                        {total.repartition.length > 0 && (
                          <Box sx={{ mt: 0.5, lineHeight: 1.3 }}>{total.repartition.join(' · ')}</Box>
                        )}
                      </>
                    ) : '—'}
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <LegendeZones table={table} />
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={menuSemaine?.ancre} open={!!menuSemaine} onClose={() => setMenuSemaine(null)}>
        <MenuItem
          selected={!!menuSemaine && !semaines.get(menuSemaine.lundi)}
          onClick={() => menuSemaine && changerSemaine(menuSemaine.lundi, null)}
        >
          <ListItemText primary="Semaine normale" secondary="Modèles répétitions et sortie continue" />
        </MenuItem>
        {TYPES_SEMAINE.map((t: { code: TypeSemaine; libelle: string; couleur: string; description: string }) => (
          <MenuItem
            key={t.code}
            selected={!!menuSemaine && semaines.get(menuSemaine.lundi) === t.code}
            onClick={() => menuSemaine && changerSemaine(menuSemaine.lundi, t.code)}
          >
            <ListItemText
              primary={<span style={{ color: t.couleur, fontWeight: 600 }}>Semaine d'{t.libelle.toLowerCase()}</span>}
              secondary={`${t.description} — répétitions : ${modeles.assimilation?.nom ?? modeles.repetitions?.nom ?? 'aucun modèle'}`}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            if (!menuSemaine) return;
            setMenuSemaine(null);
            ouvrirDecompte(menuSemaine.lundi, 'Semaine choisie', 'debut');
          }}
        >
          <ListItemText
            primary="Poser un décompte jusqu'à cette semaine"
            secondary="Cette semaine devient S1, depuis une date de début"
          />
        </MenuItem>
      </Menu>

      <DetailSeancePlanifiee
        p={detail}
        vma={vma}
        table={table}
        libelleType={detail ? typeSeance(typeDe(detail))?.libelle : null}
        chargeCorpsUA={detail ? chargeCorps(detail) : null}
        ajuster={(n: string) => afficherNotation(n, ajustementRecup)}
        onFermer={() => setDetail(null)}
        onEnvoyer={(p: SeancePlanifiee) => {
          setDetail(null);
          ouvrirEnvoi(p);
        }}
        onDecompte={lectureSeule ? undefined : (p: SeancePlanifiee) => {
          setDetail(null);
          ouvrirDecompte(p.date, `Compétition « ${p.nom} »`, 'nombre');
        }}
        onRetirer={lectureSeule || (detail && detail.date < aujourdhui) ? undefined : (p: SeancePlanifiee) => {
          setDetail(null);
          retirer(p);
        }}
        onActualiser={lectureSeule || (detail && detail.date < aujourdhui) ? undefined : (p: SeancePlanifiee) => {
          setDetail(null);
          actualiser(p);
        }}
      />

      {/* Seance de la liste : meme detail, sans date ni actions de planification */}
      <DetailSeancePlanifiee
        p={apercu ? apercuDe(apercu) : null}
        vma={vma}
        table={table}
        libelleType={apercu ? typeSeance(typeDe(apercuDe(apercu)))?.libelle : null}
        chargeCorpsUA={apercu && modeleDe(apercu) && apercu.chargeUA != null ? Math.round(apercu.chargeUA) : null}
        ajuster={(n: string) => afficherNotation(n, ajustementRecup)}
        onFermer={() => setApercu(null)}
      />

      <Dialog open={!!envoi} onClose={() => !envoiEnCours && setEnvoi(null)} fullWidth maxWidth="sm">
        <DialogTitle>Envoyer vers intervals.icu</DialogTitle>
        <DialogContent>
          {envoi && (
            <>
              <Typography sx={{ fontWeight: 600 }}>{envoi.p.nom}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {new Date(envoi.p.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                {envoi.p.categorie === 'REPOS' || envoi.p.categorie === 'NOTE' ? ' — envoyé comme note de journée' : ''}
                {envoi.p.dateEnvoiIcu ? ` — déjà envoyée le ${new Date(envoi.p.dateEnvoiIcu).toLocaleDateString('fr-FR')} : elle sera mise à jour` : ''}
              </Typography>
              {envoi.texte && (
                <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 13, whiteSpace: 'pre-wrap', maxHeight: 320, overflow: 'auto' }}>
                  {envoi.texte}
                </Box>
              )}
              {envoi.erreur && <Alert severity="error" sx={{ mt: 1.5 }}>{envoi.erreur}</Alert>}
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                Compte intervals.icu : <Link to="/preferences?onglet=intervals">Mes préférences</Link>. La séance
                apparaît ensuite dans ton calendrier intervals.icu, puis sur ta montre après synchronisation.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvoi(null)} disabled={envoiEnCours}>Annuler</Button>
          <Button variant="contained" startIcon={<EnvoiIcon />} onClick={envoyer} disabled={envoiEnCours || !envoi?.texte}>
            {envoiEnCours ? 'Envoi…' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!edition} onClose={() => setEdition(null)} fullWidth maxWidth="xs">
        <DialogTitle>Décompte de semaines</DialogTitle>
        <DialogContent>
          {edition && (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {edition.origine} : S1 = semaine du {dateLocale(edition.cible).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                fullWidth
                value={edition.mode}
                onChange={(_, mode) => mode && setEdition({ ...edition, mode })}
                sx={{ mb: 1.5 }}
              >
                <ToggleButton value="nombre">Nombre de semaines</ToggleButton>
                <ToggleButton value="debut">Date de début</ToggleButton>
              </ToggleButtonGroup>
              {edition.mode === 'nombre' ? (
                <TextField
                  autoFocus
                  fullWidth
                  type="number"
                  label="Nombre de semaines"
                  helperText="Semaine cible comprise"
                  value={edition.nombre}
                  onChange={(e) => setEdition({ ...edition, nombre: e.target.value })}
                  inputProps={{ min: 1, max: 104 }}
                />
              ) : (
                <TextField
                  autoFocus
                  fullWidth
                  type="date"
                  label="Date de début"
                  helperText="Sa semaine est la première du décompte"
                  InputLabelProps={{ shrink: true }}
                  value={edition.debut}
                  onChange={(e) => setEdition({ ...edition, debut: e.target.value })}
                />
              )}
              {debutSaisi ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  S{semainesEntre(debutSaisi, edition.cible) + 1} : semaine du {dateLocale(debutSaisi).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                  {' '}La couleur des pastilles suit le type de chaque semaine (normale, assimilation, affûtage).
                </Typography>
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {edition.mode === 'nombre' ? 'Indique un nombre de semaines entre 1 et 104.' : 'Choisis une date de début avant la semaine cible (104 semaines au plus).'}
                </Alert>
              )}
              {conflit && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Chevauche le décompte des semaines du {formaterSemaine(conflit.lundiDebut)} au {formaterSemaine(conflit.lundiCible)}.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdition(null)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrerDecompte} disabled={!debutSaisi || !!conflit}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Menu anchorEl={menuDecompte?.ancre} open={!!menuDecompte} onClose={() => setMenuDecompte(null)}>
        <MenuItem onClick={() => menuDecompte && exporterPlan(menuDecompte.decompte)}>
          <ListItemText primary="Exporter le plan" secondary="Fichier Markdown, lisible par une IA" />
        </MenuItem>
        {!lectureSeule && (
          <MenuItem
            onClick={() => {
              if (!menuDecompte) return;
              setARetirer(menuDecompte.decompte);
              setMenuDecompte(null);
            }}
          >
            <ListItemText primary="Retirer le décompte" primaryTypographyProps={{ color: 'error' }} />
          </MenuItem>
        )}
      </Menu>

      <Dialog open={!!aRetirer} onClose={() => setARetirer(null)} maxWidth="xs">
        <DialogTitle>Retirer le décompte ?</DialogTitle>
        <DialogContent>
          {aRetirer && (
            <Typography variant="body2">
              S{semainesEntre(aRetirer.lundiDebut, aRetirer.lundiCible) + 1} à S1 : semaines du {formaterSemaine(aRetirer.lundiDebut)} au {formaterSemaine(aRetirer.lundiCible)}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setARetirer(null)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={retirerDecompte}>Retirer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!noteEditee} onClose={() => setNoteEditee(null)} fullWidth maxWidth="xs">
        <DialogTitle>{editionNote ? (noteEditee?.p ? 'Modifier la note' : 'Nouvelle note') : 'Note du repos'}</DialogTitle>
        <DialogContent>
          {editionNote && (
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Titre"
              placeholder="Note"
              value={noteEditee?.nom ?? ''}
              onChange={(e) => setNoteEditee((n) => (n ? { ...n, nom: e.target.value } : n))}
              inputProps={{ maxLength: 100 }}
            />
          )}
          <TextField
            autoFocus={!editionNote}
            fullWidth
            multiline
            minRows={editionNote ? 3 : 2}
            margin="dense"
            label={editionNote ? 'Consigne' : undefined}
            required={editionNote}
            placeholder={editionNote ? 'ex : footing libre 45 min en aisance, puis 15 min d\'étirements' : 'ex : kiné, malade, voyage…'}
            value={noteEditee?.texte ?? ''}
            onChange={(e) => setNoteEditee((n) => (n ? { ...n, texte: e.target.value } : n))}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteEditee(null)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrerNote} disabled={editionNote && !noteEditee?.texte.trim()}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {message && (
        <Snackbar open autoHideDuration={6000} onClose={() => setMessage(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.texte}</Alert>
        </Snackbar>
      )}
    </Box>
  );
}
